/**
 * Maps tutorial message keys (translation keys or campaign stepIds) to voice MP3 filenames.
 * Voice files live in React/public/speech (served at /speech/).
 * Playing is cancelled when the player leaves/dismisses a message to avoid overlap.
 */

import { getEffectiveSoundOutputVolume } from '../hooks/soundEffectsManager';

const SPEECH_BASE = '/speech';

/** stepId or translation key → filename (e.g. "1.mp3") */
const VOICE_MAP = {
  // Welcome & hub (translation keys)
  'welcome.tutorial.welcome': '1.mp3',
  'welcome.tutorial.commandHub': '2.mp3',
  'battleOverview.tutorial.introText': '3.mp3',
  'battleOverview.tutorial.introTextGold': '4.mp3',
  'battleOverview.tutorial.attackText': '5.mp3',
  'dispatchAsset.tutorialText': '10.mp3',
  'lore.tutorialText': '21.mp3',
  'honorHall.tutorialText': '22.mp3',
  'marketplace.tutorialText': '28.mp3',
  'blackOps.tutorialText': '29.mp3',
  'warEffort.purposeSelectionTutorialText': '30.mp3',
  'warEffort.worldMapTutorialText': '31.mp3',
  'exploreUnknownPage.portalTutorialText': 'explore_tutorial.mp3',
  'profile.tutorialText': '32.mp3',
  'bank.tutorialText': '33.mp3',
  'strategy.tutorialText': 'tutorial_strategy_1.mp3',
  'strategy.tutorialText2': 'tutorial_strategy_2.mp3',

  // Chapter 1 Mission 1 (stepIds)
  'the_infected_attack': '34.mp3',
  'battle_zones': '35.mp3',
  'orders_1': '36.mp3',
  'orders_2': '37.mp3',
  'intel': '38.mp3',
  'health': '39.mp3',
  'deploy': '40.mp3',
  'end_turn_1': '41.mp3',
  'opponents_turn': '42.mp3',
  'special_units': '43.mp3',
  'out_of_reach': '44.mp3',
  'attack_the_general': '44_2.mp3',
  'end_turn_2': '45.mp3',
  'finish_them': '46.mp3',

  // Chapter 1 Mission 2
  'incoming_attack': '50.mp3',
  'brace_for_impact': '51.mp3',
  'counter_strike': '51_2.mp3', // Counter strike tutorial (text may be added later)
  'strike_the_berserker': '51_2.mp3',
  'deploy_your_forces': '52.mp3',
  'inferno_drop': '53.mp3',
  'shield_and_attack': '54.mp3',
  'chief_engineer_destroyed': '55.mp3',
  'duplicate': '56.mp3',
  'kill_threats': '57.mp3',
  'final_blow': '58.mp3',

  // Chapter 1 Mission 3
  'enemy_drones': '59.mp3',
  'asrahs_forces': '60.mp3',
  'confuse': '61.mp3',
  'destroy_the_drone': '62.mp3',
  'deploy_effects': '63.mp3',
  'missile_barrage': '64.mp3',
  'your_drones': '65.mp3',
  'use_your_drone': '66.mp3',
  'samuel_johnson': '67.mp3',
  'counter_drones': '68.mp3',
  'protect_your_units': '69.mp3',
  'finish_the_fight': '70.mp3',

  // Chapter 1 Mission 4 (stepIds from mission JSON)
  'mulligan': '71.mp3',
  'maat_arrives': '72.mp3',
  'maats_strength': '73.mp3',
  'devastating_impact': '74.mp3',
  'advanced_tactics': '75.mp3',
  'hold_the_line': '76.mp3',
  'mentora': '77.mp3',
  'shield_mastery': '78.mp3',
  'patience': '79.mp3',
  'clearing_the_field': '80.mp3',
  'well_done': '81.mp3',
  'reinforcements': '82.mp3',
  'harrals_betrayal': '83.mp3',
  'end_the_threat': '84.mp3',
};

/**
 * Get voice file path for a tutorial key (translation key or campaign stepId).
 * @param {string} key - e.g. 'welcome.tutorial.welcome' or 'the_infected_attack'
 * @returns {string|null} - e.g. '/speech/1.mp3' or null
 */
export function getTutorialVoicePath(key) {
  if (!key || typeof key !== 'string') return null;
  const filename = VOICE_MAP[key.trim()];
  if (!filename) return null;
  return `${SPEECH_BASE}/${filename}`;
}

/**
 * Stop and release an Audio instance so the next message can play without overlap.
 * @param {HTMLAudioElement|{ current: HTMLAudioElement | null }} audioRef
 */
export function stopTutorialVoice(audioRef) {
  const el = audioRef && (audioRef.current ?? audioRef);
  if (!el || !(el instanceof HTMLAudioElement)) return;
  try {
    el.pause();
    el.currentTime = 0;
    el.src = '';
  } catch (_) {}
}

/**
 * Play tutorial voice from path. Caller must stop previous playback before calling.
 * @param {string} path - e.g. '/speech/1.mp3'
 * @param {{ current: HTMLAudioElement | null }} audioRef - ref to store the Audio instance for later stop
 * @returns {HTMLAudioElement | null} - the Audio instance or null
 */
export function playTutorialVoice(path, audioRef) {
  if (!path || !audioRef) return null;
  const volume = getEffectiveSoundOutputVolume(1);
  if (volume === null) return null;
  try {
    const audio = new Audio(path);
    audio.volume = volume;
    audioRef.current = audio;
    audio.play().catch(() => {});
    return audio;
  } catch (_) {
    return null;
  }
}

export default { getTutorialVoicePath, stopTutorialVoice, playTutorialVoice };
